import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from an OAuth callback that lands on
    // `/app#access_token=…&provider=…`, App.js will stash the tokens and
    // reload. Skip the /me probe on that first paint so we don't race the
    // reload with a 401.
    if (window.location.hash?.includes('access_token=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        // Token invalid, try cookie-based auth
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true
          });
          setUser(response.data);
        } catch (cookieError) {
          // Clear invalid token
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setToken(null);
          setUser(null);
        }
      }
    } else {
      // Try cookie-based auth
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const signup = async (email, password, fullName, inviteCode) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, {
      email,
      password,
      full_name: fullName,
      invite_code: inviteCode || undefined,
    });
    
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const loginWithGoogle = (inviteCode) => {
    // Direct Google OAuth via our own backend. The redirect kicks off the
    // OAuth flow at /api/auth/google, which 302s to Google, then back to
    // /api/auth/google/callback, then to /app#access_token=…&provider=google.
    // App.js stashes those tokens in localStorage and reloads.
    const params = new URLSearchParams();
    if (inviteCode) params.set('invite_code', inviteCode);
    const qs = params.toString();
    window.location.href = `${API_URL}/api/auth/google${qs ? `?${qs}` : ''}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    loginWithGoogle,
    getAuthHeaders,
    checkAuth,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
